import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LANDING } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [data, setData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!data.name.trim()) e.name = "Required";
    if (!data.email.trim() || !/.+@.+\..+/.test(data.email))
      e.email = "Valid email required";
    if (!data.message.trim()) e.message = "Tell us how we can help";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      setDone(true);
      toast.success("Message sent", {
        description: "We'll get back to you within one business day.",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not send your message.";
      toast.error("Send failed", { description: String(detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={LANDING.contactSection}
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-secondary/40 border-t border-border"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="label-eyebrow mb-4"><span className="text-accent">07</span> — Talk</p>
          <h2 className="font-display text-4xl md:text-5xl h-tight font-medium">
            Prefer a quick note?
            <br />
            <span className="italic font-light text-accent">We&apos;ll write back.</span>
          </h2>
          <p className="mt-6 text-base text-muted-foreground max-w-md">
            For coverage reviews, BOR transfers, claims advocacy, or just a
            sanity check on your current program.{" "}
          </p>

          <ul className="mt-10 space-y-5 text-sm">
            <li className="flex items-start gap-3">
              <Mail size={18} className="text-accent mt-0.5" />
              <div>
                <p className="font-semibold text-foreground break-all">
                  JanayJones822@gmail.com
                </p>
                <p className="text-muted-foreground">
                  General inquiries · Quote requests
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={18} className="text-accent mt-0.5" />
              <div>
                <p className="font-semibold text-foreground">
                  (929) 456-9852
                </p>
                <p className="text-muted-foreground">
                  Monday – Friday · 8:00am – 6:00pm EST
                </p>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid={LANDING.contactForm}
            onSubmit={submit}
            className="bg-background border border-border rounded-md p-6 md:p-10 shadow-sm"
          >
            {done ? (
              <div
                data-testid={LANDING.contactSuccess}
                className="text-center py-10"
              >
                <CheckCircle2
                  size={42}
                  className="text-primary mx-auto"
                  strokeWidth={1.5}
                />
                <h3 className="font-display text-3xl font-medium mt-4">
                  Got it — talk soon.
                </h3>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                  A broker will reply within one business day.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="cname">Your name</Label>
                  <Input
                    id="cname"
                    data-testid={LANDING.contactName}
                    value={data.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="mt-2"
                    placeholder="Jane Owner"
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive mt-1">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="cemail">Email</Label>
                  <Input
                    id="cemail"
                    type="email"
                    data-testid={LANDING.contactEmail}
                    value={data.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="mt-2"
                    placeholder="jane@acme.com"
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive mt-1">
                      {errors.email}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ccompany">Company (optional)</Label>
                  <Input
                    id="ccompany"
                    data-testid={LANDING.contactCompany}
                    value={data.company}
                    onChange={(e) => set("company", e.target.value)}
                    className="mt-2"
                    placeholder="Acme Holdings, Inc."
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="cmsg">How can we help?</Label>
                  <Textarea
                    id="cmsg"
                    data-testid={LANDING.contactMessage}
                    value={data.message}
                    onChange={(e) => set("message", e.target.value)}
                    className="mt-2 min-h-[140px]"
                    placeholder="Tell us about your business, your current program, or what you'd like to fix."
                  />
                  {errors.message && (
                    <p className="text-xs text-destructive mt-1">
                      {errors.message}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={LANDING.contactSubmit}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md text-sm font-semibold hover:bg-[#1F1F1F] transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Send message"}
                    <Send size={15} />
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
